import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { ClienteFormValues } from "../ClienteFormSchema";
import { FileUploadField } from "@/components/form/FileUploadField";
import { SentDocumentField } from "@/components/form/SentDocumentField";
import { StepLayout } from "@/components/form/StepLayout";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, Download, Info, Loader2, Trash2, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { DocumentoCliente } from "@/features/clientes/types/cliente";
import { toast } from "sonner";
import { ClientesService } from "@/features/clientes/services/clientes.service";

interface DocumentsStepProps {
  form: UseFormReturn<ClienteFormValues>;
  documentos: DocumentoCliente[];
  getDocumentUrl: (path: string) => Promise<string | null>;
  isLoadingDocs: boolean;
  isEditing: boolean;
  clienteId?: string;
  onDocumentDeleted?: () => void;
}

export function DocumentsStep({ 
  form, 
  documentos, 
  getDocumentUrl, 
  isLoadingDocs, 
  isEditing,
  clienteId,
  onDocumentDeleted 
}: DocumentsStepProps) {
  const [downloadingDoc, setDownloadingDoc] = useState<string | null>(null);
  const [viewingDoc, setViewingDoc] = useState<DocumentoCliente | null>(null);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null);
  const [confirmDeleteDoc, setConfirmDeleteDoc] = useState<DocumentoCliente | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState<string | null>(null);

  const handleDownload = async (documento: DocumentoCliente) => {
    try {
      setDownloadingDoc(documento.id);
      
      const path = (documento as any).caminho_arquivo || documento.arquivo_path || (documento as any).arquivoPath;
      let fileName = documento.nome_arquivo || (documento as any).arquivoNome || 'documento';
      
      // Decodificar o nome do arquivo se estiver URL encoded
      try {
        fileName = decodeURIComponent(fileName);
      } catch (error) {
        // Se falhar na decodificação, usar o nome original
        console.warn('Erro ao decodificar nome do arquivo:', error);
      }
      
      const url = await getDocumentUrl(path);
      
      if (url) {
        // Abrir arquivo em nova aba
        window.open(url, '_blank');
        toast.success('Documento aberto em nova aba');
      } else {
        toast.error('Não foi possível gerar URL do documento');
      }
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast.error('Erro ao baixar documento');
    } finally {
      setDownloadingDoc(null);
    }
  };

  const handleView = async (documento: DocumentoCliente) => {
    try {
      setLoadingPreview(documento.id);
      
      const path = (documento as any).caminho_arquivo || documento.arquivo_path || (documento as any).arquivoPath;
      
      const url = await getDocumentUrl(path);
      
      if (url) {
        setDocumentUrl(url);
        setViewingDoc(documento);
      } else {
        toast.error('Não foi possível gerar URL do documento');
      }
    } catch (error) {
      console.error('Erro ao visualizar documento:', error);
      toast.error('Erro ao visualizar documento');
    } finally {
      setLoadingPreview(null);
    }
  };

  const handleDelete = async (documento: DocumentoCliente) => {
    try {
      setDeletingDoc(documento.id);
      
      if (!clienteId) {
        throw new Error('ID do cliente não encontrado');
      }
      
      await ClientesService.excluirDocumento(clienteId, documento.id);
      
      toast.success('Documento excluído com sucesso');
      setConfirmDeleteDoc(null);
      
      // Callback para atualizar a lista
      if (onDocumentDeleted) {
        onDocumentDeleted();
      }
    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast.error('Erro ao excluir documento');
    } finally {
      setDeletingDoc(null);
    }
  };

  const closeViewModal = () => {
    setViewingDoc(null);
    setDocumentUrl(null);
    setLoadingPreview(null);
  };

  // Função para mapear tipos de documento para nomes amigáveis
  const getDocumentTypeName = (tipo: string) => {
    const typeMap: Record<string, string> = {
      'documento_com_foto': 'Documento com Foto',
      'comprovante_residencia': 'Comprovante de Residência',
      'comprovante_pagamento': 'Comprovante de Pagamento',
      'declaracoes': 'Declarações',
      'comprovante_renda': 'Comprovante de Renda'
    };
    return typeMap[tipo] || tipo;
  };

  // O campo principal retornado pelo backend é `tipo_documento`; `tipo` é um campo legado
  // opcional que nem sempre é preenchido — por isso sempre checamos os dois.
  const getTipo = (documento: DocumentoCliente) => documento.tipo_documento || documento.tipo;

  // Verificar se documentos já existem
  const temDocumentoComFoto = documentos.some(doc => getTipo(doc) === 'documento_com_foto');
  const temComprovanteResidencia = documentos.some(doc => getTipo(doc) === 'comprovante_residencia');
  const temComprovantePagamento = documentos.some(doc => getTipo(doc) === 'comprovante_pagamento');
  const temDeclaracoes = documentos.some(doc => getTipo(doc) === 'declaracoes');
  const temComprovanteRenda = documentos.some(doc => getTipo(doc) === 'comprovante_renda');
  const todosDocumentosExistem = temDocumentoComFoto && temComprovanteResidencia &&
    temComprovantePagamento && temDeclaracoes && temComprovanteRenda;

  const getDocumentoByTipo = (tipo: string) => documentos.find(doc => getTipo(doc) === tipo);

  // Nome do arquivo já salvo para cada tipo, usado pelo FileUploadField para "lembrar" que o campo já foi preenchido
  const getExistingFileName = (tipo: string) => {
    const documento = documentos.find(doc => getTipo(doc) === tipo);
    if (!documento) return undefined;
    const raw = documento.nome_arquivo || (documento as any).arquivoNome || '';
    if (!raw) return undefined;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  };

  // Verificar se há erros nos campos de documento
  const docErrors = {
    documentoComFoto: form.formState.errors.documentoComFotoDoc?.message,
    residencia: form.formState.errors.residenciaDoc?.message,
    comprovantePagamento: form.formState.errors.comprovantePagamentoDoc?.message,
    declaracoes: form.formState.errors.declaracoesDoc?.message,
    comprovanteRenda: form.formState.errors.comprovanteRendaDoc?.message
  };

  const hasErrors = docErrors.documentoComFoto || docErrors.residencia || 
    docErrors.comprovantePagamento || docErrors.declaracoes || docErrors.comprovanteRenda;

  if (isLoadingDocs) {
    return (
      <StepLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <div className="grid gap-6 md:grid-cols-2">
            <Skeleton className="h-[200px] w-full" />
            <Skeleton className="h-[200px] w-full" />
          </div>
        </div>
      </StepLayout>
    );
  }

  return (
    <StepLayout>
      <div className="space-y-6">

        {isEditing && todosDocumentosExistem && (
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-900">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle className="text-blue-700 dark:text-blue-300">Documentos já enviados</AlertTitle>
            <AlertDescription className="text-blue-600 dark:text-blue-300">
              Você já enviou todos os documentos necessários. Caso queira atualizar algum documento, 
              basta selecionar um novo arquivo abaixo.
            </AlertDescription>
          </Alert>
        )}

        {hasErrors && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Atenção</AlertTitle>
            <AlertDescription>
              Verifique os campos de documento para corrigir os erros
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
          {/* Upload de Documento com Foto */}
          <FormField
            control={form.control}
            name="documentoComFotoDoc"
            render={({ field: { onChange, value, ...field } }) => {
              const documento = getDocumentoByTipo('documento_com_foto');
              return (
                <FormItem className="min-w-0">
                  <FormLabel className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span>Documento com Foto (RG ou CNH)</span>
                  </FormLabel>
                  {documento ? (
                    <SentDocumentField
                      fileName={getExistingFileName('documento_com_foto') || ''}
                      onView={() => handleView(documento)}
                      onDownload={() => handleDownload(documento)}
                      onDelete={() => setConfirmDeleteDoc(documento)}
                      isViewLoading={loadingPreview === documento.id}
                      isDownloadLoading={downloadingDoc === documento.id}
                      isDeleting={deletingDoc === documento.id}
                    />
                  ) : (
                    <FileUploadField
                      {...field}
                      label="Documento com Foto"
                      onChange={onChange}
                      value={value}
                      accept="image/*,.pdf"
                      hideLabel
                      required
                      error={docErrors.documentoComFoto}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Upload de Comprovante de Residência */}
          <FormField
            control={form.control}
            name="residenciaDoc"
            render={({ field: { onChange, value, ...field } }) => {
              const documento = getDocumentoByTipo('comprovante_residencia');
              return (
                <FormItem className="min-w-0">
                  <FormLabel className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span>Comprovante de Residência</span>
                  </FormLabel>
                  {documento ? (
                    <SentDocumentField
                      fileName={getExistingFileName('comprovante_residencia') || ''}
                      onView={() => handleView(documento)}
                      onDownload={() => handleDownload(documento)}
                      onDelete={() => setConfirmDeleteDoc(documento)}
                      isViewLoading={loadingPreview === documento.id}
                      isDownloadLoading={downloadingDoc === documento.id}
                      isDeleting={deletingDoc === documento.id}
                    />
                  ) : (
                    <FileUploadField
                      {...field}
                      label="Comprovante de Residência"
                      onChange={onChange}
                      value={value}
                      accept="image/*,.pdf"
                      hideLabel
                      required
                      error={docErrors.residencia}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Novos campos de documentos obrigatórios */}
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
          {/* Upload de Comprovante de Pagamento */}
          <FormField
            control={form.control}
            name="comprovantePagamentoDoc"
            render={({ field: { onChange, value, ...field } }) => {
              const documento = getDocumentoByTipo('comprovante_pagamento');
              return (
                <FormItem className="min-w-0">
                  <FormLabel className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span>Comprovante de Pagamento</span>
                  </FormLabel>
                  {documento ? (
                    <SentDocumentField
                      fileName={getExistingFileName('comprovante_pagamento') || ''}
                      onView={() => handleView(documento)}
                      onDownload={() => handleDownload(documento)}
                      onDelete={() => setConfirmDeleteDoc(documento)}
                      isViewLoading={loadingPreview === documento.id}
                      isDownloadLoading={downloadingDoc === documento.id}
                      isDeleting={deletingDoc === documento.id}
                    />
                  ) : (
                    <FileUploadField
                      {...field}
                      label="Comprovante de Pagamento"
                      onChange={onChange}
                      value={value}
                      accept="image/*,.pdf"
                      hideLabel
                      required
                      error={docErrors.comprovantePagamento}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Upload de Declarações */}
          <FormField
            control={form.control}
            name="declaracoesDoc"
            render={({ field: { onChange, value, ...field } }) => {
              const documento = getDocumentoByTipo('declaracoes');
              return (
                <FormItem className="min-w-0">
                  <FormLabel className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span>Declarações (Carta de Pagamento por Terceiros)</span>
                  </FormLabel>
                  {documento ? (
                    <SentDocumentField
                      fileName={getExistingFileName('declaracoes') || ''}
                      onView={() => handleView(documento)}
                      onDownload={() => handleDownload(documento)}
                      onDelete={() => setConfirmDeleteDoc(documento)}
                      isViewLoading={loadingPreview === documento.id}
                      isDownloadLoading={downloadingDoc === documento.id}
                      isDeleting={deletingDoc === documento.id}
                    />
                  ) : (
                    <FileUploadField
                      {...field}
                      label="Declarações"
                      onChange={onChange}
                      value={value}
                      accept="image/*,.pdf"
                      hideLabel
                      required
                      error={docErrors.declaracoes}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Upload de Comprovante de Renda */}
          <FormField
            control={form.control}
            name="comprovanteRendaDoc"
            render={({ field: { onChange, value, ...field } }) => {
              const documento = getDocumentoByTipo('comprovante_renda');
              return (
                <FormItem className="min-w-0">
                  <FormLabel className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span>Comprovante de Renda</span>
                  </FormLabel>
                  {documento ? (
                    <SentDocumentField
                      fileName={getExistingFileName('comprovante_renda') || ''}
                      onView={() => handleView(documento)}
                      onDownload={() => handleDownload(documento)}
                      onDelete={() => setConfirmDeleteDoc(documento)}
                      isViewLoading={loadingPreview === documento.id}
                      isDownloadLoading={downloadingDoc === documento.id}
                      isDeleting={deletingDoc === documento.id}
                    />
                  ) : (
                    <FileUploadField
                      {...field}
                      label="Comprovante de Renda"
                      onChange={onChange}
                      value={value}
                      accept="image/*,.pdf"
                      hideLabel
                      required
                      error={docErrors.comprovanteRenda}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </div>

      {/* Modal de Visualização */}
      <Dialog open={!!viewingDoc} onOpenChange={closeViewModal}>
        <DialogContent className="z-[2000] w-[95vw] max-w-6xl h-[95vh] max-h-[95vh] flex flex-col p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="text-lg">
              {viewingDoc && getDocumentTypeName(getTipo(viewingDoc) || '')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 min-h-0 p-4">
            {loadingPreview !== null ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
                  <p className="text-gray-600">Carregando documento...</p>
                </div>
              </div>
            ) : documentUrl ? (
              <div className="w-full h-full">
                {(() => {
                  const fileName = viewingDoc?.nome_arquivo || (viewingDoc as any)?.arquivoNome || '';
                  const isPDF = fileName.toLowerCase().endsWith('.pdf');
                  
                  if (isPDF) {
                    return (
                      <iframe
                        src={documentUrl}
                        className="w-full h-full border rounded-md"
                        title="Visualização do documento PDF"
                        style={{ minHeight: 'calc(95vh - 200px)' }}
                      />
                    );
                  } else {
                    return (
                      <div className="w-full h-full flex items-center justify-center border rounded-md bg-muted/40">
                        <img
                          src={documentUrl}
                          alt="Documento"
                          className="max-w-full max-h-full object-contain rounded-md shadow-sm"
                          style={{ maxHeight: 'calc(95vh - 200px)' }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            const parent = (e.target as HTMLImageElement).parentElement;
                            if (parent) {
                              parent.innerHTML = `
                                <div class="text-center">
                                  <div class="h-12 w-12 mx-auto mb-4 text-gray-400">📄</div>
                                  <p class="text-gray-600">Não foi possível carregar a imagem</p>
                                  <p class="text-sm text-gray-500 mt-2">Tente fazer o download do arquivo</p>
                                </div>
                              `;
                            }
                          }}
                        />
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">Não foi possível carregar o documento</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/40">
            <div className="flex gap-2 w-full justify-end">
              <Button type="button" variant="outline" onClick={closeViewModal}>
                <X className="h-4 w-4 mr-2" />
                Fechar
              </Button>
              <Button 
                type="button" 
                onClick={() => viewingDoc && handleDownload(viewingDoc)}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!!downloadingDoc}
              >
                {downloadingDoc ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Baixando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Baixar
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={!!confirmDeleteDoc} onOpenChange={() => setConfirmDeleteDoc(null)}>
        <DialogContent className="z-[2000]">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600">
              <strong>Documento:</strong> {confirmDeleteDoc && getDocumentTypeName(getTipo(confirmDeleteDoc) || '')}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Enviado em:</strong> {confirmDeleteDoc && new Date(confirmDeleteDoc.created_at).toLocaleDateString()}
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              type="button"
              variant="outline" 
              onClick={() => setConfirmDeleteDoc(null)}
            >
              Cancelar
            </Button>
            <Button 
              type="button"
              variant="destructive" 
              onClick={() => confirmDeleteDoc && handleDelete(confirmDeleteDoc)}
              disabled={deletingDoc === confirmDeleteDoc?.id}
            >
              {deletingDoc === confirmDeleteDoc?.id ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Excluindo...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StepLayout>
  );
}
